/**
 * DashboardLayout Component
 *
 * Main layout wrapper for Service Provider Dashboard.
 * PURELY PRESENTATIONAL - receives all data as props, emits events via callbacks.
 *
 * @example
 * ```tsx
 * <DashboardLayout
 *   navigationSections={navigationSections}
 *   userProfile={userProfile}
 *   isSidebarCollapsed={false}
 *   onToggleSidebar={() => setCollapsed(!collapsed)}
 * >
 *   <Outlet />
 * </DashboardLayout>
 * ```
 */

import { ReactNode, useState } from 'react';
import { Sidebar } from './Sidebar';
import type { NavigationSection, UserProfileViewModel } from './types';

export interface DashboardLayoutProps {
  /** Navigation sections to display in sidebar */
  navigationSections: NavigationSection[];
  /** User profile information */
  userProfile: UserProfileViewModel;
  /** Child content to render in main area */
  children: ReactNode;
  /** Optional header content */
  header?: ReactNode;
  /** Optional notification/alert banner */
  banner?: ReactNode;
}

/**
 * Main dashboard layout with sidebar navigation and content area.
 * Responsive design with mobile-friendly collapsed sidebar.
 */
export function DashboardLayout({
  navigationSections,
  userProfile,
  children,
  header,
  banner,
}: DashboardLayoutProps) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-neutral-50 lg:flex">
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:z-auto ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar
          sections={navigationSections}
          userProfile={userProfile}
          onClose={() => setIsMobileSidebarOpen(false)}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 lg:flex lg:flex-col">
        {/* Mobile Header with Menu Toggle */}
        <div className="sticky top-0 z-30 bg-white border-b border-neutral-200 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="p-2 rounded-md hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Open sidebar"
            >
              <svg
                className="w-6 h-6 text-neutral-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <span className="text-lg font-semibold text-neutral-900">
              CalWMP
            </span>
            <div className="w-10" /> {/* Spacer for alignment */}
          </div>
        </div>

        {/* Optional Banner (alerts, notifications) */}
        {banner && <div className="sticky top-0 z-20">{banner}</div>}

        {/* Optional Page Header */}
        {header && <div className="bg-white border-b border-neutral-200">{header}</div>}

        {/* Main Content */}
        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
